import {
  Activity,
  Beaker,
  ClipboardCheck,
  FileText,
  Home,
  MapPin,
  Moon,
  Store,
  Timer,
  type LucideIcon,
} from "lucide-react-native";
import type { DataPointSampleType, TestBody } from "@src/packages/content";
import type { TranslationFunction } from "@src/packages/i18n";

type TestQuickFact = NonNullable<TestBody["quickFacts"]>[number];

/**
 * One tile in a test sheet's At a glance grid: a quick fact from the content,
 * or a fact the sheet adds about where the test is done and who sells it.
 */
export type TestGlanceFact = {
  key: string;
  icon: LucideIcon;
  label: string;
  value: string;
};

/** Where a test is done, as the At a glance grid says it. */
export type TestPlace = "home" | "clinic" | "home-or-clinic" | "lab-or-home";

export const TEST_QUICK_FACT_LABEL_KEYS: Record<TestQuickFact["kind"], string> =
  {
    sample: "learn.test_detail.quick_fact_sample",
    time: "learn.test_detail.quick_fact_time",
    pain: "learn.test_detail.quick_fact_pain",
    anesthesia: "learn.test_detail.quick_fact_anesthesia",
    prep: "learn.test_detail.quick_fact_prep",
    results: "learn.test_detail.quick_fact_results",
  };

export const TEST_QUICK_FACT_ICONS: Record<TestQuickFact["kind"], LucideIcon> =
  {
    sample: Beaker,
    time: Timer,
    pain: Activity,
    anesthesia: Moon,
    prep: ClipboardCheck,
    results: FileText,
  };

/** A test's own quick facts, as At a glance tiles with their icons. */
export function testQuickFactsAsGlanceFacts(
  t: TranslationFunction,
  items: TestQuickFact[],
): TestGlanceFact[] {
  return items.map((item, index) => ({
    key: `${item.kind}-${index}`,
    icon: TEST_QUICK_FACT_ICONS[item.kind],
    label: item.label ?? t(TEST_QUICK_FACT_LABEL_KEYS[item.kind]),
    value: item.value,
  }));
}

export const SAMPLE_TYPE_LABEL_KEYS: Record<DataPointSampleType, string> = {
  blood: "care.lab_result.sample_blood",
  saliva: "care.lab_result.sample_saliva",
  urine: "care.lab_result.sample_urine",
  semen: "care.lab_result.sample_semen",
  ultrasound: "care.lab_result.sample_ultrasound",
  imaging: "care.lab_result.sample_imaging",
  genetic: "care.lab_result.sample_genetic",
  swab: "care.lab_result.sample_swab",
  tissue: "care.lab_result.sample_tissue",
};

const TEST_PLACE_KEYS: Record<TestPlace, string> = {
  home: "learn.test_detail.done_at_home",
  clinic: "learn.test_detail.done_at_a_clinic_visit",
  "home-or-clinic": "learn.test_detail.done_at_home_or_a_clinic_visit",
  "lab-or-home": "learn.test_detail.done_at_a_lab_or_at_home",
};

export function hasSampleQuickFact(body: TestBody): boolean {
  return body.quickFacts?.some((fact) => fact.kind === "sample") ?? false;
}

export function whereTestIsDoneFact(
  t: TranslationFunction,
  place: TestPlace,
): TestGlanceFact {
  return {
    key: "where",
    icon: place === "clinic" ? MapPin : Home,
    label: t("learn.test_detail.where_it_is_done"),
    value: t(TEST_PLACE_KEYS[place]),
  };
}

export function soldByFact(
  t: TranslationFunction,
  company: string,
): TestGlanceFact {
  return {
    key: "sold-by",
    icon: Store,
    label: t("learn.test_detail.sold_by"),
    value: company,
  };
}

/** The sample, for a test whose quick facts do not already name it. */
export function sampleFact(
  t: TranslationFunction,
  value: string,
): TestGlanceFact {
  return {
    key: "sample",
    icon: Beaker,
    label: t(TEST_QUICK_FACT_LABEL_KEYS.sample),
    value,
  };
}

/** The quiet line under a tracked marker's name: "Blood · ng/mL". */
export function dataPointMetaLine(
  t: TranslationFunction,
  sampleTypes: DataPointSampleType[] | undefined,
  unit: string | undefined,
): string {
  return [
    ...(sampleTypes ?? []).map((sampleType) =>
      t(SAMPLE_TYPE_LABEL_KEYS[sampleType]),
    ),
    ...(unit ? [unit] : []),
  ].join(" · ");
}
